/**
 * =============================================================================
 * TERRAKITE BRANDING CONFIGURATION
 * =============================================================================
 *
 * Centralized branding configuration for the entire application.
 * All brand-related text, URLs, contact info, and metadata should be defined here.
 *
 * To rebrand the application, simply update the values in this file.
 * All components importing brandingConfig will automatically reflect the changes.
 *
 * @version 1.0.0
 * @lastUpdated 2026-02-17
 */

export const brandingConfig = {
  // ===========================
  // CORE BRAND IDENTITY
  // ===========================

  /** Short brand name (used in headers, logos, compact spaces) */
  brandName: 'TerraKite',

  /** Full brand name (used in formal contexts) */
  brandNameFull: 'TerraKite',

  /** Legal company name (used in terms, privacy policy, invoices) */
  companyLegalName: 'TerraKite Logistics Private Limited',

  /** Brand tagline - primary */
  tagline: 'Fast, no-hub delivery at the Speed of Now',

  /** Brand tagline - short version */
  taglineShort: 'Delivering at the Speed of Now',

  /** Brand description - used in meta descriptions and about sections */
  description:
    'TerraKite is a revolutionary delivery platform offering fast, direct deliveries without traditional hubs or warehouses. Experience instant pickup and delivery like never before.',

  /** Short description for PWA manifest and compact displays */
  descriptionShort: 'No warehouse. No hub. Just fast, direct delivery.',

  // ===========================
  // APP METADATA & SEO
  // ===========================

  metadata: {
    /** Default page title */
    defaultTitle: 'TerraKite - Fast, Direct Delivery at the Speed of Now',

    /** Title template for page-specific titles */
    titleTemplate: '%s | TerraKite Delivery',

    /** Site name for Open Graph */
    siteName: 'TerraKite Delivery Platform',

    /** Primary site URL */
    siteUrl: 'https://terrakite.com',

    /** Keywords for SEO */
    keywords: [
      'terrakite',
      'fast delivery',
      'direct delivery',
      'instant delivery',
      'same day delivery',
      'logistics',
      'courier',
      'parcel delivery',
      'last mile delivery',
      'on-demand delivery',
      'relay delivery',
    ],

    /** Open Graph image configuration */
    ogImage: {
      url: '/assets/images/og-image.png',
      width: 1200,
      height: 630,
      alt: 'TerraKite Delivery - Fast, Direct, Reliable',
    },

    /** Twitter card type */
    twitterCard: 'summary_large_image',

    /** Locale */
    locale: 'en_IN',

    /** Theme color for browser chrome */
    themeColor: '#FF9800',
  },

  // ===========================
  // CONTACT INFORMATION
  // ===========================

  contact: {
    /** Support email address */
    supportEmail: 'hello@terrakite.com',

    /** General inquiries email */
    infoEmail: 'hello@terrakite.com',

    /** Business/partnerships email */
    businessEmail: 'hello@terrakite.com',

    /** Primary support phone number (formatted) */
    supportPhone: '+91 9885761260',

    /** Support phone number (raw, for tel: links) */
    supportPhoneRaw: '+919885761260',

    /** WhatsApp number (raw, for wa.me links) */
    whatsappNumber: '919885761260',

    /** WhatsApp full URL */
    whatsappUrl: 'https://wa.me/919885761260',

    /** Physical address */
    address: {
      street: '',
      city: 'Visakhapatnam',
      state: 'Andhra Pradesh',
      country: 'India',
      postalCode: '530001',
      full: 'Visakhapatnam, Andhra Pradesh 530001, India',
    },
  },

  // ===========================
  // SOCIAL MEDIA HANDLES
  // ===========================

  social: {
    twitter: {
      handle: '@Terrakite_',
      url: 'https://twitter.com/Terrakite_',
    },
    facebook: {
      handle: 'TerrakiteDelivery',
      url: 'https://facebook.com/TerrakiteDelivery',
    },
    instagram: {
      handle: '@terrakite_',
      url: 'https://instagram.com/terrakite_',
    },
    linkedin: {
      handle: 'terrakite-logistics',
      url: 'https://linkedin.com/company/terrakite-logistics',
    },
    youtube: {
      handle: '@TerrakiteDelivery',
      url: 'https://youtube.com/@TerrakiteDelivery',
    },
  },

  // ===========================
  // LEGAL & COMPLIANCE
  // ===========================

  legal: {
    /** Company name for legal documents */
    companyName: 'Terrakite Logistics Private Limited',

    /** Copyright text */
    copyright: `© ${new Date().getFullYear()} Terrakite Logistics. All rights reserved.`,

    /** Copyright year (for manual formatting) */
    copyrightYear: new Date().getFullYear(),

    /** Terms of service URL path */
    termsUrl: '/terms',

    /** Privacy policy URL path */
    privacyUrl: '/privacy',

    /** Refund policy URL path */
    refundUrl: '/refund-policy',

    /** Cookie policy URL path */
    cookieUrl: '/cookie-policy',
  },

  // ===========================
  // ASSETS & MEDIA
  // ===========================

  assets: {
    /** Primary logo (dark backgrounds) */
    logoDark: '/assets/terrakite-teal-logo.png',

    /** Primary logo (light backgrounds) */
    logoLight: '/assets/terrakite-teal-logo.png',

    /** Square logo/icon */
    logoSquare: '/assets/terrakite-logo.png',

    /** Favicon */
    favicon: '/assets/favicon.ico',

    /** Apple touch icon */
    appleTouchIcon: '/assets/apple-touch-icon.png',

    /** Logo alt text */
    logoAlt: 'TerraKite Logo',
  },

  // ===========================
  // PWA MANIFEST VALUES
  // ===========================

  pwa: {
    name: 'TerraKite - No-Hub Delivery Service',
    shortName: 'TerraKite',
    description: 'Fast, no-hub delivery at the Speed of Now',
    startUrl: '/',
    display: 'standalone',
    backgroundColor: '#0a0a0f',
    themeColor: '#FF9800',
  },

  // ===========================
  // PAGE TITLES
  // ===========================

  pageTitles: {
    home: 'Home - TerraKite',
    login: 'Login - TerraKite',
    register: 'Register - TerraKite',
    dashboard: 'Dashboard - TerraKite',
    orders: 'My Orders - TerraKite',
    activeParcels: 'Active Parcels - TerraKite',
    history: 'Booking History - TerraKite',
    wallet: 'My Wallet - TerraKite',
    tracking: 'Track Delivery - TerraKite',
    support: 'Help & Support - TerraKite',
    settings: 'Settings - TerraKite',
    profile: 'Profile - TerraKite',
    terms: 'Terms of Service - TerraKite',
    privacy: 'Privacy Policy - TerraKite',
    landing: 'TerraKite - Fast, Direct Delivery at the Speed of Now',
    default: 'TerraKite - Fast Delivery Service',
  },

  // ===========================
  // UI TEXT & MESSAGES
  // ===========================

  messages: {
    /** Welcome message */
    welcome: 'Welcome to TerraKite',

    /** WhatsApp share message prefix */
    whatsappSharePrefix: '🚚 *TerraKite Delivery Update*',

    /** WhatsApp share message suffix */
    whatsappShareSuffix: '_Thank you for using TerraKite!_',

    /** Join rider CTA */
    joinRiderCta: 'Join TerraKite Riders',

    /** Why choose us heading */
    whyChooseUs: 'Why choose TerraKite?',

    /** Footer tagline */
    footerTagline: 'Delivering at the speed of now.',
  },

  // ===========================
  // STORAGE KEYS (for consistency)
  // ===========================

  storage: {
    prefix: 'terrakite_',
    cookieConsent: 'terrakite_cookie_consent',
    userSession: 'terrakite_session',
    chunkReload: 'terrakite_chunk_reload_ts',
  },
};

// ===========================
// HELPER FUNCTIONS
// ===========================

/**
 * Get formatted page title
 * @param {string} pageName - The name of the page
 * @returns {string} - Formatted page title
 */
export const getPageTitle = (pageName) => {
  return brandingConfig.pageTitles[pageName] || brandingConfig.pageTitles.default;
};

/**
 * Get WhatsApp share link with pre-filled message
 * @param {string} message - The message to share
 * @returns {string} - WhatsApp URL with message
 */
export const getWhatsAppShareUrl = (message) => {
  const encodedMessage = encodeURIComponent(message);
  return `${brandingConfig.contact.whatsappUrl}?text=${encodedMessage}`;
};

/**
 * Get tel: link for phone
 * @returns {string} - tel: URL
 */
export const getPhoneLink = () => {
  return `tel:${brandingConfig.contact.supportPhoneRaw}`;
};

/**
 * Get mailto: link for email
 * @param {string} emailType - Type of email (support, info, business)
 * @returns {string} - mailto: URL
 */
export const getEmailLink = (emailType = 'support') => {
  const emailMap = {
    support: brandingConfig.contact.supportEmail,
    info: brandingConfig.contact.infoEmail,
    business: brandingConfig.contact.businessEmail,
  };
  return `mailto:${emailMap[emailType] || emailMap.support}`;
};

export default brandingConfig;
